
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, DollarSign, Mail, Star, MessageSquare, Users, GraduationCap, TrendingUp, TrendingDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";

const AdminInsights = () => {
  const { toast } = useToast();
  const [insights, setInsights] = useState({
    registeredStudents: 0,
    activeStudents: 0,
    enrolledStudents: 0,
    totalEarnings: 0,
    newsletterEmails: 0,
    averageRating: 0,
    totalFeedback: 0,
    contactApplications: 0,
    isLoading: true,
  });

  const [trends, setTrends] = useState({
    registeredStudents: 0,
    activeStudents: 0,
    enrolledStudents: 0,
    totalEarnings: 0,
    newsletterEmails: 0,
    averageRating: 0,
    totalFeedback: 0,
    contactApplications: 0,
  });

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        // Current data
        const currentDate = new Date();
        const yesterdayDate = new Date(currentDate);
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        
        // Format dates for database queries
        const todayStart = new Date(currentDate.setHours(0, 0, 0, 0)).toISOString();
        const yesterdayStart = new Date(yesterdayDate.setHours(0, 0, 0, 0)).toISOString();
        
        // Fetch registered users count
        const { count: registeredStudents, error: usersError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        if (usersError) throw usersError;

        // Fetch yesterday's registered users count
        const { count: yesterdayRegisteredStudents, error: yesterdayUsersError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .lt('created_at', todayStart);

        if (yesterdayUsersError) throw yesterdayUsersError;

        // Fetch active users (users with status = 'active')
        const { count: activeStudents, error: activeError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active');

        if (activeError) throw activeError;

        // Fetch yesterday's active users
        const { count: yesterdayActiveStudents, error: yesterdayActiveError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active')
          .lt('created_at', todayStart);

        if (yesterdayActiveError) throw yesterdayActiveError;

        // Fetch enrolled students count (unique users in course_enrollments)
        const { data: enrollmentData, error: enrollmentError } = await supabase
          .from('course_enrollments')
          .select('user_id')
          .limit(1000);

        if (enrollmentError) throw enrollmentError;

        // Get unique enrolled students
        const uniqueEnrolledStudents = new Set(enrollmentData.map(item => item.user_id)).size;

        // Fetch yesterday's enrolled students
        const { data: yesterdayEnrollmentData, error: yesterdayEnrollmentError } = await supabase
          .from('course_enrollments')
          .select('user_id')
          .lt('enrolled_at', todayStart)
          .limit(1000);

        if (yesterdayEnrollmentError) throw yesterdayEnrollmentError;

        const yesterdayUniqueEnrolledStudents = new Set(yesterdayEnrollmentData.map(item => item.user_id)).size;

        // Fetch total earnings
        const { data: paymentsData, error: paymentsError } = await supabase
          .from('course_payments')
          .select('amount')
          .eq('status', 'completed');

        if (paymentsError) throw paymentsError;

        const totalEarnings = paymentsData.reduce((sum, payment) => sum + Number(payment.amount), 0);

        // Fetch yesterday's total earnings
        const { data: yesterdayPaymentsData, error: yesterdayPaymentsError } = await supabase
          .from('course_payments')
          .select('amount')
          .eq('status', 'completed')
          .lt('created_at', todayStart);

        if (yesterdayPaymentsError) throw yesterdayPaymentsError;

        const yesterdayTotalEarnings = yesterdayPaymentsData.reduce((sum, payment) => sum + Number(payment.amount), 0);

        // Fetch newsletter subscribers count
        const { count: newsletterEmails, error: newsletterError } = await supabase
          .from('newsletter_subscribers')
          .select('*', { count: 'exact', head: true })
          .is('unsubscribed_at', null);

        if (newsletterError) throw newsletterError;

        // Fetch yesterday's newsletter subscribers count
        const { count: yesterdayNewsletterEmails, error: yesterdayNewsletterError } = await supabase
          .from('newsletter_subscribers')
          .select('*', { count: 'exact', head: true })
          .is('unsubscribed_at', null)
          .lt('subscribed_at', todayStart);

        if (yesterdayNewsletterError) throw yesterdayNewsletterError;

        // Fetch feedback count (combine blog and lesson feedback)
        const { count: blogFeedback, error: blogFeedbackError } = await supabase
          .from('blog_feedback')
          .select('*', { count: 'exact', head: true });

        if (blogFeedbackError) throw blogFeedbackError;

        const { count: lessonFeedback, error: lessonFeedbackError } = await supabase
          .from('lesson_feedback')
          .select('*', { count: 'exact', head: true });

        if (lessonFeedbackError) throw lessonFeedbackError;

        // Fetch yesterday's feedback count
        const { count: yesterdayBlogFeedback, error: yesterdayBlogFeedbackError } = await supabase
          .from('blog_feedback')
          .select('*', { count: 'exact', head: true })
          .lt('created_at', todayStart);

        if (yesterdayBlogFeedbackError) throw yesterdayBlogFeedbackError;

        const { count: yesterdayLessonFeedback, error: yesterdayLessonFeedbackError } = await supabase
          .from('lesson_feedback')
          .select('*', { count: 'exact', head: true })
          .lt('created_at', todayStart);

        if (yesterdayLessonFeedbackError) throw yesterdayLessonFeedbackError;

        // Fetch average ratings (combine blog and lesson ratings)
        const { data: blogRatings, error: blogRatingsError } = await supabase
          .from('blog_ratings')
          .select('rating');

        if (blogRatingsError) throw blogRatingsError;

        const { data: lessonRatings, error: lessonRatingsError } = await supabase
          .from('lesson_ratings')
          .select('rating');

        if (lessonRatingsError) throw lessonRatingsError;

        // Calculate average rating
        const allRatings = [...blogRatings, ...lessonRatings];
        const averageRating = allRatings.length > 0 
          ? (allRatings.reduce((sum, item) => sum + item.rating, 0) / allRatings.length).toFixed(1)
          : 0;

        // Fetch yesterday's ratings
        const { data: yesterdayBlogRatings, error: yesterdayBlogRatingsError } = await supabase
          .from('blog_ratings')
          .select('rating')
          .lt('created_at', todayStart);

        if (yesterdayBlogRatingsError) throw yesterdayBlogRatingsError;

        const { data: yesterdayLessonRatings, error: yesterdayLessonRatingsError } = await supabase
          .from('lesson_ratings')
          .select('rating')
          .lt('created_at', todayStart);

        if (yesterdayLessonRatingsError) throw yesterdayLessonRatingsError;

        const yesterdayAllRatings = [...yesterdayBlogRatings, ...yesterdayLessonRatings];
        const yesterdayAverageRating = yesterdayAllRatings.length > 0
          ? (yesterdayAllRatings.reduce((sum, item) => sum + item.rating, 0) / yesterdayAllRatings.length).toFixed(1)
          : 0;

        // Fetch contact messages count
        const { count: contactApplications, error: contactError } = await supabase
          .from('contact_messages')
          .select('*', { count: 'exact', head: true });

        if (contactError) throw contactError;

        // Fetch yesterday's contact messages count
        const { count: yesterdayContactApplications, error: yesterdayContactError } = await supabase
          .from('contact_messages')
          .select('*', { count: 'exact', head: true })
          .lt('created_at', todayStart);

        if (yesterdayContactError) throw yesterdayContactError;

        // Calculate percentage changes
        const calculatePercentageChange = (current, previous) => {
          if (previous === 0) return current > 0 ? 100 : 0;
          return ((current - previous) / previous) * 100;
        };

        const trendsData = {
          registeredStudents: calculatePercentageChange(registeredStudents, yesterdayRegisteredStudents),
          activeStudents: calculatePercentageChange(activeStudents, yesterdayActiveStudents),
          enrolledStudents: calculatePercentageChange(uniqueEnrolledStudents, yesterdayUniqueEnrolledStudents),
          totalEarnings: calculatePercentageChange(totalEarnings, yesterdayTotalEarnings),
          newsletterEmails: calculatePercentageChange(newsletterEmails, yesterdayNewsletterEmails),
          averageRating: calculatePercentageChange(Number(averageRating), Number(yesterdayAverageRating)),
          totalFeedback: calculatePercentageChange((blogFeedback || 0) + (lessonFeedback || 0), 
                                                 (yesterdayBlogFeedback || 0) + (yesterdayLessonFeedback || 0)),
          contactApplications: calculatePercentageChange(contactApplications, yesterdayContactApplications),
        };

        setInsights({
          registeredStudents: registeredStudents || 0,
          activeStudents: activeStudents || 0,
          enrolledStudents: uniqueEnrolledStudents,
          totalEarnings,
          newsletterEmails: newsletterEmails || 0,
          averageRating: Number(averageRating),
          totalFeedback: (blogFeedback || 0) + (lessonFeedback || 0),
          contactApplications: contactApplications || 0,
          isLoading: false,
        });

        setTrends(trendsData);
      } catch (error) {
        console.error('Error fetching insights:', error);
        toast({
          title: "Error loading insights",
          description: "Failed to load some dashboard data.",
          variant: "destructive",
        });
        setInsights(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchInsights();
  }, [toast]);

  const TrendIndicator = ({ value }) => {
    if (value === 0) return null;
    
    const isPositive = value > 0;
    return (
      <Badge 
        variant={isPositive ? "green" : "destructive"}
        className="ml-2 flex items-center gap-1"
      >
        {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        <span>{Math.abs(value).toFixed(1)}%</span>
      </Badge>
    );
  };

  const StatCard = ({ title, value, icon: Icon, suffix = "", trend = 0, isLoading = false }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="flex items-center text-2xl font-bold">
          {isLoading ? (
            <div className="h-8 w-24 animate-pulse rounded bg-muted" />
          ) : (
            <>
              <span>
                {value}
                {suffix && <span className="text-sm text-muted-foreground ml-1">{suffix}</span>}
              </span>
              <TrendIndicator value={trend} />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-4">Dashboard Insights</h2>
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard 
          title="Registered Students" 
          value={insights.registeredStudents} 
          icon={User}
          trend={trends.registeredStudents}
          isLoading={insights.isLoading} 
        />
        <StatCard 
          title="Active Students" 
          value={insights.activeStudents} 
          icon={Users}
          trend={trends.activeStudents}
          isLoading={insights.isLoading} 
        />
        <StatCard 
          title="Enrolled Students" 
          value={insights.enrolledStudents} 
          icon={GraduationCap}
          trend={trends.enrolledStudents}
          isLoading={insights.isLoading} 
        />
        <StatCard 
          title="Total Earnings" 
          value={insights.totalEarnings} 
          icon={DollarSign}
          suffix="EGP"
          trend={trends.totalEarnings}
          isLoading={insights.isLoading} 
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard 
          title="Newsletter Subscribers" 
          value={insights.newsletterEmails} 
          icon={Mail}
          trend={trends.newsletterEmails}
          isLoading={insights.isLoading} 
        />
        <StatCard 
          title="Average Rating" 
          value={insights.averageRating} 
          icon={Star}
          suffix="/ 5"
          trend={trends.averageRating}
          isLoading={insights.isLoading} 
        />
        <StatCard 
          title="Total Feedback" 
          value={insights.totalFeedback} 
          icon={MessageSquare}
          trend={trends.totalFeedback}
          isLoading={insights.isLoading} 
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1">
        <StatCard 
          title="Contact Applications" 
          value={insights.contactApplications} 
          icon={MessageSquare}
          trend={trends.contactApplications}
          isLoading={insights.isLoading} 
        />
      </div>
    </div>
  );
};

export default AdminInsights;
